import { backgroundOrange, gold, orange } from '@/theme/colors'
import Modal from '@components/Modal'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

const DAY_NAME_MAX = 100
const NOTE_MAX = 2000

interface EditDayInfoModalProps {
	isOpen: boolean
	onClose: () => void
	onSave: (name: string | null, note: string | null) => void
	isSaving: boolean
	initialName?: string | null
	initialNote?: string | null
	dayLabel: string
}

const EditDayInfoModal: React.FC<EditDayInfoModalProps> = ({
	isOpen,
	onClose,
	onSave,
	isSaving,
	initialName,
	initialNote,
	dayLabel,
}) => {
	const [name, setName] = useState(initialName || '')
	const [note, setNote] = useState(initialNote || '')

	useEffect(() => {
		if (isOpen) {
			setName(initialName || '')
			setNote(initialNote || '')
		}
	}, [isOpen, initialName, initialNote])

	const handleSave = (e: React.FormEvent) => {
		e.preventDefault()
		onSave(name.trim() || null, note.trim() || null)
	}

	const handleCancel = () => {
		setName(initialName || '')
		setNote(initialNote || '')
		onClose()
	}

	const formId = 'edit-day-info-form'

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleCancel}
			title={`Day Info — ${dayLabel}`}
			closeOnBackdropClick={false}
			backgroundColor={backgroundOrange}
			size="medium"
			footer={
				<Actions>
					<CancelButton type="button" onClick={handleCancel} disabled={isSaving}>
						Cancel
					</CancelButton>
					<SaveButton type="submit" form={formId} disabled={isSaving}>
						{isSaving ? 'Saving…' : 'Save'}
					</SaveButton>
				</Actions>
			}
		>
			<Form id={formId} onSubmit={handleSave}>
				<FieldGroup>
					<Label htmlFor="day-name">Day Title</Label>
					<SubLabel>Give this day a custom name (e.g. "Beach Day")</SubLabel>
					<Input
						id="day-name"
						type="text"
						value={name}
						onChange={e => setName(e.target.value.slice(0, DAY_NAME_MAX))}
						placeholder="Add a title for this day…"
						maxLength={DAY_NAME_MAX}
					/>
					<CharCount $warn={name.length > DAY_NAME_MAX * 0.9}>
						{name.length} / {DAY_NAME_MAX}
					</CharCount>
				</FieldGroup>

				<FieldGroup>
					<Label htmlFor="day-note">Notes</Label>
					<SubLabel>Add any notes or reminders for this day</SubLabel>
					<Textarea
						id="day-note"
						value={note}
						onChange={e => setNote(e.target.value.slice(0, NOTE_MAX))}
						placeholder="Add a note for this day…"
						rows={6}
						maxLength={NOTE_MAX}
					/>
					<CharCount $warn={note.length > NOTE_MAX * 0.9}>
						{note.length} / {NOTE_MAX}
					</CharCount>
				</FieldGroup>

			</Form>
		</Modal>
	)
}

const Form = styled.form`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
`

const FieldGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
`

const Label = styled.label`
	font-size: 0.875rem;
	font-weight: 600;
	color: #2c3e50;
`

const SubLabel = styled.span`
	font-size: 0.8rem;
	color: #6c757d;
	margin-bottom: 0.25rem;
`

const Input = styled.input`
	padding: 0.625rem 0.75rem;
	border: 1.5px solid #e1e5e9;
	border-radius: 8px;
	font-size: 0.9rem;
	font-family: inherit;
	color: #2c3e50;
	background: white;
	transition: border-color 0.2s ease;

	&:focus {
		outline: none;
		border-color: ${orange};
	}

	&::placeholder {
		color: #adb5bd;
	}
`

const Textarea = styled.textarea`
	padding: 0.625rem 0.75rem;
	border: 1.5px solid #e1e5e9;
	border-radius: 8px;
	font-size: 0.9rem;
	font-family: inherit;
	color: #2c3e50;
	background: white;
	resize: vertical;
	min-height: 120px;
	transition: border-color 0.2s ease;
	line-height: 1.5;

	&:focus {
		outline: none;
		border-color: ${orange};
	}

	&::placeholder {
		color: #adb5bd;
	}
`

const CharCount = styled.span<{ $warn: boolean }>`
	font-size: 0.75rem;
	color: ${props => (props.$warn ? orange : '#adb5bd')};
	text-align: right;
`

const Actions = styled.div`
	display: flex;
	gap: 0.75rem;
	justify-content: flex-end;
	padding-top: 0.5rem;
`

const CancelButton = styled.button`
	padding: 0.625rem 1.25rem;
	border: 1.5px solid #ced4da;
	border-radius: 8px;
	background: white;
	color: #6c757d;
	font-size: 0.9rem;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
	font-family: inherit;

	&:hover:not(:disabled) {
		border-color: #adb5bd;
		color: #495057;
	}

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
`

const SaveButton = styled.button`
	padding: 0.625rem 1.5rem;
	border: none;
	border-radius: 8px;
	background: ${gold};
	color: white;
	font-size: 0.9rem;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
	font-family: inherit;

	&:hover:not(:disabled) {
		background: ${orange};
	}

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
`

export default EditDayInfoModal
